import { useState } from 'react';
import { useProspectingAvailability } from './useProspectingAvailability';
import { copyMessageLabel } from './copyMessages';
import { Link } from 'react-router';
import type { ReactNode } from 'react';
import { InfoHint, Skeleton } from '../../components/ui';
import {
  activityFunnel,
  awaitingCounts,
  dailyRows,
  replyRateAllTime,
  useAllActivity,
} from './activityStats';
import { DashboardSection } from './DashboardSection';
import { WhatMovedBlock } from './WhatMoved';
import { whatMoved } from './dashboardWork';
import {
  COHORT_LABELS,
  cohortFlow,
  cohortStock,
  readCollapsedSections,
  toggleSection,
  writeCollapsedSections,
} from './dashboardSections';
import type { LeadCohort } from './dashboardSections';
import {
  DEFAULT_RANGE,
  RANGE_LABELS,
  RANGE_ORDER,
  rangeBounds,
  rangeSpanLabel,
} from './dashboardRange';
import type { RangeKey } from './dashboardRange';
import { useLeadDealOutcomes } from './dealOutcomes';
import { GROUP_HINTS, METRIC_HINTS } from './metricHints';
import {
  useAllMemberships,
  useCopySets,
  useLeadLists,
  useRecentDeals,
  useSalesTasks,
} from './hooks';
import { canSee, useMyPermissions } from '../auth/useMyPermissions';
import { shortDate, stageName, todayIso } from './trackerFormat';
import { kpiValueClass } from './kpiValue';
import { channelCounts, leadName, reportStats, sendsPerList } from './types';
import { duplicateLeadGroups } from './leadDuplicates';
import { drillOf } from './drill';
import type { Drill } from './drill';
import type { Lead, LeadStage } from './types';

export function DashboardView({
  leads,
  stages,
  onOpenLead,
  onDrill,
}: {
  leads: Lead[];
  stages: LeadStage[];
  onOpenLead: (id: string) => void;
  /**
   * A number was clicked. Optional so the view still renders in a harness that
   * does not care, which is how the existing dashboard tests call it.
   */
  onDrill?: (drill: Drill) => void;
}) {
  // Every alert shows. Kyu named "what needs attention right now?" as the
  // FIRST thing the dashboard must answer, and a screen that shows one of
  // three problems with the rest behind a "+2 more" is not answering it. This
  // reverses the decision made when the banners were built; the question it
  // was optimising for — screen space above the numbers — is not the question
  // being asked of this page now.
  const [bannersOpen, setBannersOpen] = useState(true);
  const { message_enabled } = useProspectingAvailability();
  /**
   * The range the summary tier is read through.
   *
   * Not persisted, deliberately. Kyu: "I want to keep the system lean and
   * avoid clutter." A remembered range is a second per-device store and a
   * second thing that can be stale when you open the page — and unlike the
   * fold state, the cost of getting it wrong is a number that means something
   * other than what you assume it means.
   */
  const [range, setRange] = useState<RangeKey>(DEFAULT_RANGE);
  // Which lower boxes this person has folded away. Read once on mount and
  // written back on every change — see dashboardSections.ts for why it is
  // per-device and why they all start collapsed.
  const [collapsedSections, setCollapsedSections] = useState<string[]>(() =>
    readCollapsedSections(),
  );
  const toggleCollapsed = (key: string) => {
    setCollapsedSections((current) => {
      const next = toggleSection(current, key);
      writeCollapsedSections(next);
      return next;
    });
  };
  const isCollapsed = (key: string) => collapsedSections.includes(key);
  const membershipsQuery = useAllMemberships();
  const copySetsQuery = useCopySets();
  const tasksQuery = useSalesTasks();
  const listsQuery = useLeadLists();
  // Batch 4: every lead-level number below comes from the activity timeline
  // and the trigger-maintained lead counters — the same sources the Leads
  // list reads, so the two screens can no longer disagree about who was
  // touched. Memberships stay loaded for the per-LIST tables only: a cycle is
  // a fact about a list, and the membership row is its record.
  const activityQuery = useAllActivity();
  const permissions = useMyPermissions();
  // `deals` is behind a RESTRICTIVE module gate, so this returns nothing for a
  // sales member without Deals rather than erroring. The query is skipped and
  // the block says so, instead of printing a zero that means "you cannot see
  // this".
  const dealsVisible = canSee(permissions.data, 'deals');
  const dealsQuery = useRecentDeals(dealsVisible);
  // Won is derived off the linked deal, not off a lead status — same source
  // and same rule as the Pipeline's Won band, so the two screens cannot
  // disagree about how many we have won.
  const outcomesQuery = useLeadDealOutcomes();

  if (membershipsQuery.isPending || copySetsQuery.isPending || activityQuery.isPending) {
    return <Skeleton lines={5} label="Loading dashboard" />;
  }

  const memberships = membershipsQuery.data ?? [];
  const copySets = copySetsQuery.data ?? [];
  const salesTasks = tasksQuery.data ?? [];
  const lists = listsQuery.data ?? [];
  const tasksDue = salesTasks.filter(
    (task) => task.status === 'open' && task.due_on !== null && task.due_on <= todayIso(),
  ).length;
  const activities = activityQuery.data ?? [];
  // Stage counts come from lead rows and copy-set replies from cycles — both
  // still honest sources. The touch and reply numbers it used to feed are the
  // ones that moved to the timeline.
  const stats = reportStats(leads, memberships, stages);
  const duplicates = duplicateLeadGroups(leads);
  // The total and the converted count now come from cohortStats('all'), so
  // the three summary sections and the split line below them can only ever
  // have been computed the same way.
  const channels = channelCounts(leads);

  // KPI: Reply rate — replies over TOUCHED, with touched read from the lead
  // counters and replied from the one union definition in activityStats.
  const rate = replyRateAllTime(leads, activities, memberships);

  // Today + trailing 14 days, from the timeline.
  const dailyAll = dailyRows(leads, activities, memberships, todayIso());
  const today = dailyAll[0] ?? { date: todayIso(), newLeads: 0, touchesSent: 0, replies: 0 };
  const daily = dailyAll;
  const funnel = activityFunnel(leads, activities, memberships, stages);
  const listSends = sendsPerList(memberships);
  const listName = (id: string) => String(lists.find((l) => l.id === id)?.name ?? 'Unknown list');

  // Both awaiting numbers, labelled. One question, two honest answers: the
  // inbound queue that needs somebody today, and the unworked pile behind it.
  const awaiting = awaitingCounts(leads, stages);

  // Converted to a deal was three guesses at a display name — 'Active Deal',
  // 'Deal', 'Qualified' — and counted zero, silently, if none matched. It now
  // goes through leadsInStage inside cohortStats, once per cohort.

  // Funnel — largest count sets the 100% bar
  const maxStageCount = Math.max(...stats.byStage.map((s) => s.count), 1);

  // Copy performance — map id → name
  const setName = (id: string | null) =>
    id === null
      ? 'No copy set'
      : (() => {
          const copy = copySets.find((s) => s.id === id);
          return copy ? (message_enabled ? copyMessageLabel(copy) : copy.name) : 'Unknown';
        })();

  // Recent activity: last 8 leads added (created_at desc — leads are fetched desc)
  const recentLeads = leads.slice(0, 8);

  // Inbound consent records — the permanent opt-ins, always visible
  const inboundLeads = leads.filter((lead) => lead.source === 'inbound');

  // ── The summary tier ─────────────────────────────────────────────────────
  //
  // The selected range, and the two kinds of number it may and may not touch.
  // dashboardSections.ts carries the full reasoning; the short version is that
  // nothing in the database records when a lead entered a status, so the
  // pipeline counts are labelled "now" rather than windowed on a date they do
  // not have.
  const deals = dealsQuery.data ?? [];
  const bounds = rangeBounds(range, todayIso());
  const spanLabel = rangeSpanLabel(bounds);
  const moved = whatMoved(activities, memberships, deals, leads, bounds);

  const cohorts: LeadCohort[] = ['all', 'inbound', 'outbound'];
  // Membership goes through leadChannel in cohortLeads, so "Not set" can never
  // land in Outbound — in the flow figures as well as the stock ones.
  const flow = {
    all: cohortFlow(leads, activities, memberships, deals, 'all', bounds),
    inbound: cohortFlow(leads, activities, memberships, deals, 'inbound', bounds),
    outbound: cohortFlow(leads, activities, memberships, deals, 'outbound', bounds),
  };
  // undefined while it loads, NOT an empty map. An empty map would make every
  // Won tile read 0, which is a wrong answer rather than a slow one.
  const outcomes = outcomesQuery.data;
  const stock = {
    all: cohortStock(leads, stages, outcomes, 'all'),
    inbound: cohortStock(leads, stages, outcomes, 'inbound'),
    outbound: cohortStock(leads, stages, outcomes, 'outbound'),
  };

  /**
   * The alert banners, in the order they deserve attention.
   *
   * ALL of them show. They used to stack, then only the first was shown with
   * the rest behind a "+N more" — a reasonable trade when the dashboard's job
   * was to start with the numbers.
   *
   * It is not the job any more. Kyu named "what needs attention right now?"
   * as the first question this page must answer, and a screen showing one of
   * three problems is not answering it. The control stays, so a quiet morning
   * can still be folded away by hand.
   *
   * The order is by how fast the thing decays:
   *   1. A task due today or already overdue. It is the only one of the three
   *      with a clock on it, and the clock has already run out.
   *   2. Inbound awaiting a first touch. A warm lead cools; this is the one
   *      that drives the day's outreach.
   *   3. Possible duplicates. Real, and nothing about it gets worse if it
   *      waits until tomorrow.
   */
  /**
   * A clickable number, or the plain number when nothing is listening.
   *
   * `why` comes from METRIC_HINTS where the tile already has one, so the
   * sentence a person reads in the ⓘ is the same sentence the drill banner
   * shows on the other tab. One definition, two places to meet it.
   */
  function drillTo(label: string, why: string, ids: readonly string[]): void {
    onDrill?.(drillOf({ label, why, kind: 'lead', ids }));
  }

  const banners: { key: string; tone?: 'brand'; body: ReactNode }[] = [];
  if (tasksDue > 0) {
    banners.push({
      key: 'tasks',
      body: (
        <>
          <span aria-hidden="true">🗒</span>
          <span>
            {tasksDue} task{tasksDue === 1 ? '' : 's'} due today or overdue —{' '}
            {/* Sales Tasks is its own page since 14 September, and it holds
                deal and prospect tasks this banner's count cannot see. A
                link, not a tab switch: the destination is no longer here. */}
            <Link to="/sales-tasks" className="tracker-lead-link">
              open Sales Tasks
            </Link>
          </span>
        </>
      ),
    });
  }
  if (awaiting.all > 0) {
    banners.push({
      key: 'awaiting',
      tone: 'brand',
      body: (
        <>
          <span aria-hidden="true">✉</span>
          <span>
            <strong>{awaiting.inbound}</strong> inbound awaiting first touch —{' '}
            {/* Opens those leads. It used to switch to the Leads tab with no
                filter, landing somebody on 488 rows to find 156 by eye, which
                is the complaint this whole feature came from. */}
            <button
              type="button"
              className="tracker-lead-link"
              onClick={() => {
                drillTo(
                  'Inbound awaiting first touch',
                  METRIC_HINTS.awaitingFirstTouch,
                  awaiting.inboundIds,
                );
              }}
            >
              view them
            </button>
            {' · '}
            {awaiting.all} on New in total, mostly the unworked outbound pile. Two different
            questions, so both numbers are shown.
          </span>
        </>
      ),
    });
  }
  if (duplicates.length > 0) {
    banners.push({
      key: 'dupes',
      body: (
        <>
          <span aria-hidden="true">⚠</span>
          <span>
            {duplicates.length} possible duplicate{duplicates.length === 1 ? '' : 's'} detected —{' '}
            <button
              type="button"
              className="tracker-lead-link"
              onClick={() => {
                // Every lead in every flagged group, not one per group: merging
                // needs both sides on screen, and "open a lead to merge" left
                // the reader hunting for the other one.
                drillTo(
                  'Possible duplicates',
                  'Leads whose name or email matches another lead closely enough to be the same person. Both sides of every pair are here.',
                  duplicates.flat().map((lead) => lead.id),
                );
              }}
            >
              open them
            </button>
            {' to merge.'}
          </span>
        </>
      ),
    });
  }
  const hidden = banners.length - 1;

  return (
    <div>
      {/* Alert banners: the most urgent one, and a way to the rest. */}
      {banners.length === 0 ? null : (
        <div className="tracker-banners">
          {(bannersOpen ? banners : banners.slice(0, 1)).map((banner) => (
            <div
              key={banner.key}
              className={`tracker-dupes-banner${
                banner.tone === 'brand' ? ' tracker-dupes-banner--brand' : ''
              }`}
            >
              {banner.body}
            </div>
          ))}
          {hidden > 0 && (
            <button
              type="button"
              className="tracker-banners-more"
              aria-expanded={bannersOpen}
              onClick={() => setBannersOpen((open) => !open)}
            >
              {bannersOpen ? 'Show less' : `+${String(hidden)} more`}
            </button>
          )}
        </div>
      )}

      {/* ── The primary surface ────────────────────────────────────────────
          Kyu, 2026-08-27: All Leads / Inbound / Outbound "should read as the
          main command-level sections of the page, not as plain summary
          strips", and the page should have "more contrast between primary
          summary and secondary expandable blocks".

          Two things caused the strip feeling, and both are structural rather
          than cosmetic. The figures rendered through .tracker-inbound-stat,
          which is a BASELINE-ALIGNED INLINE RUN — value and label side by
          side, on one line, in a card whose padding had been deliberately
          reduced for holding a single row. Six of those is a sentence of
          numbers. And nothing on the page separated the summary from the seven
          analysis boxes, so all ten blocks carried the same weight.

          So the figures move onto .tracker-kpi-card — the card object this
          codebase already has, label over value, figure at 2xl — and the two
          tiers get a heading each. */}
      <section className="dash-summary">
        <header className="dash-summary-head">
          <div>
            <h2 className="dash-summary-title">Overview</h2>
            {/* Today, as one line rather than a card. It answers "what has
                happened since I woke up", which no range answers, but it is
                not a command-level section and it should not sit above the
                three that are. Previously a full card at the top of the page,
                competing with them for the same job. */}
            <p className="dash-today">
              Today: {today.newLeads} new · {today.touchesSent} touches · {today.replies} replies ·{' '}
              {tasksDue} tasks due
            </p>
          </div>
          {/* The range control. A segmented switch rather than a dropdown,
              the same shape as the theme switch and the CG pipeline switcher,
              for the reason recorded there: a small set of mutually exclusive
              states you should be able to see yourself in without pressing
              anything. */}
          <div className="dash-range-switch" role="group" aria-label="Time range">
            {RANGE_ORDER.map((key) => (
              <button
                key={key}
                type="button"
                className={`dash-range-option${range === key ? ' dash-range-option--on' : ''}`}
                aria-pressed={range === key}
                onClick={() => {
                  setRange(key);
                }}
              >
                {RANGE_LABELS[key]}
              </button>
            ))}
          </div>
        </header>

        {/* ── The label row ──────────────────────────────────────────────────
            Kyu, 31 Aug: the three cohorts have to be readable "in 1 glance
            without having to scroll".

            The same two labels were printed inside all three cards — six
            copies of two strings, 18px each. Said once here they cost 18px
            total and they turn three separate cards into three rows of one
            comparison, because the columns below line up under them.

            Below 1100px this is hidden and each card prints its own pair
            again: the stylesheet stacks the runs at that width, so a label row
            up here would no longer sit over the run it names. Exactly one of
            the two is ever displayed, so nothing is read out twice. */}
        <div className="dash-cohort-legend">
          <p className="dash-group-label">
            Activity {RANGE_LABELS[range]} · {spanLabel}
            <InfoHint label="Activity" text={GROUP_HINTS.activity} />
          </p>
          <p className="dash-group-label">
            In pipeline now
            <InfoHint label="In pipeline now" text={GROUP_HINTS.pipeline} />
          </p>
        </div>

        {cohorts.map((cohort) => (
          <section key={cohort} className="dash-card dash-cohort">
            <header className="dash-card-head">
              <h3 className="dash-card-title">{COHORT_LABELS[cohort]}</h3>
              {/* The channel split used to be a footer line under All Leads —
                  "146 inbound · 543 outbound". Those two numbers are the
                  totals of the two cards directly below it, so the line was
                  saying twice what the stack already says, and it cost the
                  tallest card 32px it needed to stay above the fold. What is
                  left is what nothing else reports: the leads with no channel
                  set, and the all-time reply rate. */}
              <p className="dash-card-span">
                {cohort === 'all'
                  ? `${String(stock.all.total)} leads${
                      channels.notSet === 0 ? '' : ` · ${String(channels.notSet)} not set`
                    } · reply rate ${rate.label} all time`
                  : `${String(stock[cohort].total)} leads in total`}
              </p>
            </header>

            <div className="dash-cohort-cols">
              <div className="dash-cohort-col">
                {/* ── Ranged, because every figure below has a real date ───────
                arrivals from leads.created_at, touches / replies / meetings
                from the activity timeline, conversions from deals.created_at.
                Nothing here is inferred from a lead's current status. */}
                <p className="dash-group-label">
                  Activity {RANGE_LABELS[range]} · {spanLabel}
                  <InfoHint label="Activity" text={GROUP_HINTS.activity} />
                </p>
                <div className="dash-metric-grid">
                  <div className="tracker-kpi-card">
                    <div className="tracker-kpi-label">
                      Arrived
                      <InfoHint label="Arrived" text={METRIC_HINTS.arrived} />
                    </div>
                    <div className={kpiValueClass(flow[cohort].arrived)}>
                      {flow[cohort].arrived}
                    </div>
                  </div>
                  <div className="tracker-kpi-card">
                    <div className="tracker-kpi-label">
                      Touches
                      <InfoHint label="Touches" text={METRIC_HINTS.touches} />
                    </div>
                    <div className={kpiValueClass(flow[cohort].touches)}>
                      {flow[cohort].touches}
                    </div>
                  </div>
                  <div className="tracker-kpi-card">
                    <div className="tracker-kpi-label">
                      Replies
                      <InfoHint label="Replies" text={METRIC_HINTS.replies} />
                    </div>
                    <div className={kpiValueClass(flow[cohort].replies)}>
                      {flow[cohort].replies}
                    </div>
                  </div>
                  <div className="tracker-kpi-card">
                    <div className="tracker-kpi-label">
                      Meetings
                      {/* Last tile in the run when the deals gate hides Converted,
                      so the bubble has to anchor from the right edge there. */}
                      <InfoHint
                        label="Meetings"
                        text={METRIC_HINTS.meetings}
                        align={dealsVisible ? 'start' : 'end'}
                      />
                    </div>
                    <div className={kpiValueClass(flow[cohort].meetings)}>
                      {flow[cohort].meetings}
                    </div>
                  </div>
                  {/* Absent, not zero, for somebody who cannot read `deals`. The
                  restrictive module gate returns an empty list rather than an
                  error, so a printed 0 would say "none converted" to a person
                  who simply cannot see them. Same rule as What moved. */}
                  {dealsVisible && (
                    <div className="tracker-kpi-card">
                      <div className="tracker-kpi-label">
                        Converted
                        <InfoHint label="Converted" text={METRIC_HINTS.converted} align="end" />
                      </div>
                      <div className={kpiValueClass(flow[cohort].converted)}>
                        {flow[cohort].converted}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="dash-cohort-col dash-cohort-col--stock">
                {/* ── NOT ranged, and labelled so nobody assumes otherwise ─────
                These are counts of where leads are sitting right now. No
                column anywhere records when a lead entered a status, and the
                one log that records stage moves stores the status's display
                NAME across a rename. Kyu, 2026-08-27: "do not fake time-range
                movement on figures that are really 'current state' counts." */}
                <p className="dash-group-label">
                  In pipeline now
                  <InfoHint label="In pipeline now" text={GROUP_HINTS.pipeline} />
                </p>
                <div className="dash-metric-grid">
                  <div className="tracker-kpi-card">
                    <div className="tracker-kpi-label">
                      Awaiting 1st touch
                      <InfoHint label="Awaiting 1st touch" text={METRIC_HINTS.awaitingFirstTouch} />
                    </div>
                    <button
                      type="button"
                      className={`${kpiValueClass(stock[cohort].awaitingFirstTouch)} tracker-kpi-open`}
                      aria-label={`Open the ${String(stock[cohort].awaitingFirstTouch)} leads behind Awaiting 1st touch`}
                      onClick={() => {
                        drillTo(
                          `Awaiting 1st touch · ${COHORT_LABELS[cohort]}`,
                          METRIC_HINTS.awaitingFirstTouch,
                          stock[cohort].awaitingFirstTouchIds,
                        );
                      }}
                    >
                      {stock[cohort].awaitingFirstTouch}
                    </button>
                  </div>
                  <div className="tracker-kpi-card">
                    <div className="tracker-kpi-label">
                      In talks
                      <InfoHint label="In talks" text={METRIC_HINTS.inTalks} />
                    </div>
                    <button
                      type="button"
                      className={`${kpiValueClass(stock[cohort].inTalks)} tracker-kpi-open`}
                      aria-label={`Open the ${String(stock[cohort].inTalks)} leads behind In talks`}
                      onClick={() => {
                        drillTo(
                          `In talks · ${COHORT_LABELS[cohort]}`,
                          METRIC_HINTS.inTalks,
                          stock[cohort].inTalksIds,
                        );
                      }}
                    >
                      {stock[cohort].inTalks}
                    </button>
                  </div>
                  <div className="tracker-kpi-card">
                    <div className="tracker-kpi-label">
                      Active deals
                      <InfoHint label="Active deals" text={METRIC_HINTS.activeDeals} />
                    </div>
                    <button
                      type="button"
                      className={`${kpiValueClass(stock[cohort].activeDeals)} tracker-kpi-open`}
                      aria-label={`Open the ${String(stock[cohort].activeDeals)} leads behind Active deals`}
                      onClick={() => {
                        drillTo(
                          `Active deals · ${COHORT_LABELS[cohort]}`,
                          METRIC_HINTS.activeDeals,
                          stock[cohort].activeDealsIds,
                        );
                      }}
                    >
                      {stock[cohort].activeDeals}
                    </button>
                  </div>
                  <div className="tracker-kpi-card">
                    {/* Won replaced Closed here on 1 Sep, on Kyu's call. Closed
                    was the lead STATUS meaning no longer pursued — nearer to
                    lost than to won — and he read it as "won and lost". Won is
                    not a lead status at all: it rolls up from the linked deal,
                    which is why it needed the outcomes query rather than a
                    relabelled tile. */}
                    <div className="tracker-kpi-label">
                      Won
                      <InfoHint label="Won" text={METRIC_HINTS.won} align="end" />
                    </div>
                    {stock[cohort].wonUnknown ? (
                      // Not a button while the outcomes are in flight: a tile
                      // reading — has no list to open, and one that opened an
                      // empty table would look like an answer.
                      <div className={kpiValueClass(stock[cohort].won)}>—</div>
                    ) : (
                      <button
                        type="button"
                        className={`${kpiValueClass(stock[cohort].won)} tracker-kpi-open`}
                        aria-label={`Open the ${String(stock[cohort].won)} leads behind Won`}
                        onClick={() => {
                          drillTo(
                            `Won · ${COHORT_LABELS[cohort]}`,
                            METRIC_HINTS.won,
                            stock[cohort].wonIds,
                          );
                        }}
                      >
                        {stock[cohort].won}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        <WhatMovedBlock
          moved={moved}
          rangeLabel={RANGE_LABELS[range]}
          spanLabel={spanLabel}
          dealsVisible={dealsVisible}
          onOpenLead={onOpenLead}
        />
      </section>

      {/* ── The secondary surface ──────────────────────────────────────────
          Kyu: "These are secondary inspection panels, not the primary
          dashboard surface." They all arrive collapsed now. The note is not
          decoration: each of these boxes has its OWN window baked into it
          (Daily Activity is 14 days, Recent Leads is the last eight added),
          and the first thing anybody does after changing the range above is
          wonder why the funnel did not move. */}
      <h2 className="dash-tier-title">Analysis &amp; reports</h2>
      <p className="dash-tier-note">
        Collapsed by default. Each box keeps its own window — the range above does not change them.
      </p>

      {/* One card of fold rows (Drop 36a). It was three separate grids plus a
          bare wrapper, which drew as three cards once the boxes lost their
          borders — measured live. */}
      <div className="tracker-dashboard-grid">
        {/* Stage funnel */}
        <DashboardSection
          title="Stage Funnel"
          collapsed={isCollapsed('stage-funnel')}
          onToggle={() => {
            toggleCollapsed('stage-funnel');
          }}
        >
          <div className="tracker-funnel">
            {stats.byStage.map((row) => (
              <div key={row.name} className="tracker-funnel-row">
                <span className="tracker-funnel-label" title={row.name}>
                  {row.name}
                </span>
                <div className="tracker-funnel-track">
                  <div
                    className="tracker-funnel-fill"
                    style={{ width: `${Math.round((row.count / maxStageCount) * 100)}%` }}
                  />
                </div>
                <span className="tracker-funnel-count">{row.count}</span>
              </div>
            ))}
            {stats.byStage.length === 0 && (
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
                No stages yet.
              </p>
            )}
          </div>
        </DashboardSection>

        {/* Copy performance */}
        <DashboardSection
          title="Copy Performance"
          collapsed={isCollapsed('copy-performance')}
          onToggle={() => {
            toggleCollapsed('copy-performance');
          }}
        >
          {stats.copySetReplies.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
              No outreach data yet.
            </p>
          ) : (
            <table className="tracker-perf-table">
              <thead>
                <tr>
                  <th>Copy Set</th>
                  <th>Leads</th>
                  <th>Replies</th>
                  <th>Rate</th>
                </tr>
              </thead>
              <tbody>
                {stats.copySetReplies.map((entry) => (
                  <tr key={entry.copySetId ?? 'none'}>
                    <td>{setName(entry.copySetId)}</td>
                    <td>{entry.leads}</td>
                    <td>{entry.replies}</td>
                    <td>
                      {entry.leads === 0
                        ? '—'
                        : `${Math.round((entry.replies / entry.leads) * 100)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DashboardSection>

        {/* Conversion funnel + sends per list */}
        <DashboardSection
          title="Conversion Funnel"
          collapsed={isCollapsed('conversion-funnel')}
          onToggle={() => {
            toggleCollapsed('conversion-funnel');
          }}
        >
          <div className="tracker-funnel">
            {funnel.map((row) => (
              <div key={row.label} className="tracker-funnel-row">
                <span className="tracker-funnel-label" title={row.label}>
                  {row.label}
                </span>
                <div className="tracker-funnel-track">
                  <div
                    className="tracker-funnel-fill"
                    style={{
                      width: `${
                        (funnel[0]?.count ?? 0) === 0
                          ? 0
                          : Math.round((row.count / (funnel[0]?.count ?? 1)) * 100)
                      }%`,
                    }}
                  />
                </div>
                <span className="tracker-funnel-count">
                  {row.count}
                  {row.pctOfTouched === null ? '' : ` · ${row.pctOfTouched}%`}
                </span>
              </div>
            ))}
          </div>
        </DashboardSection>

        <DashboardSection
          title="Sends Per List"
          collapsed={isCollapsed('sends-per-list')}
          onToggle={() => {
            toggleCollapsed('sends-per-list');
          }}
        >
          {listSends.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
              No sends logged yet.
            </p>
          ) : (
            <table className="tracker-perf-table">
              <thead>
                <tr>
                  <th>List</th>
                  <th>T1</th>
                  <th>T2</th>
                  <th>T3</th>
                  <th>T4</th>
                  <th>Replies</th>
                </tr>
              </thead>
              <tbody>
                {listSends.map((entry) => (
                  <tr key={entry.listId}>
                    <td>{listName(entry.listId)}</td>
                    <td>{entry.sends[0]}</td>
                    <td>{entry.sends[1]}</td>
                    <td>{entry.sends[2]}</td>
                    <td>{entry.sends[3]}</td>
                    <td>{entry.replies}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DashboardSection>

        {/* Daily activity, trailing 14 days */}
        <DashboardSection
          title="Daily Activity (Last 14 Days)"
          collapsed={isCollapsed('daily-activity')}
          onToggle={() => {
            toggleCollapsed('daily-activity');
          }}
        >
          <table className="tracker-perf-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>New Leads</th>
                <th>Touches Sent</th>
                <th>Replies</th>
              </tr>
            </thead>
            <tbody>
              {daily.map((row) => (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td>{row.newLeads === 0 ? '—' : row.newLeads}</td>
                  <td>{row.touchesSent === 0 ? '—' : row.touchesSent}</td>
                  <td>{row.replies === 0 ? '—' : row.replies}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DashboardSection>

        {/* Recent leads + Inbound consent records */}
        <DashboardSection
          title="Recent Leads"
          collapsed={isCollapsed('recent-leads')}
          onToggle={() => {
            toggleCollapsed('recent-leads');
          }}
        >
          {recentLeads.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
              No leads yet.
            </p>
          ) : (
            <ul className="tracker-activity-list">
              {recentLeads.map((lead) => (
                <li key={lead.id} className="tracker-activity-row">
                  <span className="tracker-activity-dot" />
                  <button
                    type="button"
                    className="tracker-lead-link tracker-activity-name"
                    onClick={() => onOpenLead(lead.id)}
                  >
                    {leadName(lead)}
                  </button>
                  <span className="tracker-activity-meta">
                    {lead.company_name === '' ? '' : `${lead.company_name} · `}
                    {shortDate(lead.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DashboardSection>

        <DashboardSection
          title="Inbound Leads (Consent Records)"
          collapsed={isCollapsed('inbound-consent')}
          onToggle={() => {
            toggleCollapsed('inbound-consent');
          }}
        >
          {inboundLeads.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
              No inbound leads yet.
            </p>
          ) : (
            <table className="tracker-perf-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Stage</th>
                </tr>
              </thead>
              <tbody>
                {inboundLeads.map((lead) => (
                  <tr key={lead.id}>
                    <td>
                      <button
                        type="button"
                        className="tracker-lead-link"
                        onClick={() => onOpenLead(lead.id)}
                      >
                        {leadName(lead)}
                      </button>
                    </td>
                    <td>{lead.company_name}</td>
                    <td>{stageName(stages, lead.stage_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DashboardSection>
      </div>
    </div>
  );
}

// ── Lead slide-in panel ───────────────────────────────────────────────────────
